import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm no-underline">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentCategory.name} - 磁力链指南`,
          description: currentCategory.description,
          url: `https://magnetlink-guide.com/category/${slug}`,
          inLanguage: 'zh-CN',
        })}
      </script>

      {/* Category Header */}
      <section className="pt-24 pb-10 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="mb-4">
            <Breadcrumb items={getCategoryBreadcrumb(slug || '')} />
          </div>

          <div className="flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
              <i className={`${currentCategory.icon} text-2xl`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
                {currentCategory.description}
              </p>
              <p className="text-xs text-foreground-400 mt-2">
                共 {categoryArticles.length} 篇文章
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 no-underline ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Articles List */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-list-3-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col sm:flex-row bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 no-underline"
                >
                  <div className="sm:w-52 flex-shrink-0 aspect-[16/10] sm:aspect-auto overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 p-5 flex flex-col justify-between">
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.summary}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                      <span>{article.readTime}</span>
                      <span>·</span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}